import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DeleteIcon from '@mui/icons-material/Delete';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const VendorAllocations = () => {
    const { id: vendorId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [vendor, setVendor] = useState(null);
    const [stores, setStores] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [sales, setSales] = useState([]);

    const [storeId, setStoreId] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productSearch, setProductSearch] = useState("");
    const [productOptions, setProductOptions] = useState([]);
    const [quantity, setQuantity] = useState("");
    const [allocating, setAllocating] = useState(false);

    const [saleDialog, setSaleDialog] = useState(null); // the allocation being sold from
    const [saleQty, setSaleQty] = useState("");
    const [loggingSale, setLoggingSale] = useState(false);

    const fetchAll = async () => {
        try {
            const [vendorRes, storesRes, allocRes, salesRes] = await Promise.all([
                axios.get(`/api/v1/admin/vendor/${vendorId}`),
                axios.get('/api/v1/admin/stores'),
                axios.get(`/api/v1/admin/vendor/${vendorId}/allocations`),
                axios.get(`/api/v1/admin/vendor/${vendorId}/sales`),
            ]);
            setVendor(vendorRes.data.vendor);
            setStores(storesRes.data.stores);
            setAllocations(allocRes.data.allocations);
            setSales(salesRes.data.sales);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load vendor data", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    useEffect(() => {
        if (!productSearch) { setProductOptions([]); return; }
        const timeout = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/v1/products', { params: { keyword: productSearch } });
                setProductOptions(data.products || []);
            } catch (error) { /* ignore */ }
        }, 400);
        return () => clearTimeout(timeout);
    }, [productSearch]);

    const allocateHandler = async (e) => {
        e.preventDefault();
        if (!storeId || !selectedProduct || !quantity || Number(quantity) < 0) {
            enqueueSnackbar("Please choose a store, product, and quantity", { variant: "error" });
            return;
        }
        setAllocating(true);
        try {
            await axios.post(`/api/v1/admin/vendor/${vendorId}/allocation`, {
                storeId, productId: selectedProduct._id, quantity: Number(quantity),
            });
            enqueueSnackbar("Allocation saved", { variant: "success" });
            setSelectedProduct(null);
            setQuantity("");
            setProductSearch("");
            fetchAll();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save allocation", { variant: "error" });
        } finally {
            setAllocating(false);
        }
    };

    const deleteAllocation = async (allocId) => {
        if (!window.confirm("Remove this allocation entirely?")) return;
        try {
            await axios.delete(`/api/v1/admin/vendor/allocation/${allocId}`);
            enqueueSnackbar("Allocation removed", { variant: "success" });
            fetchAll();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to remove allocation", { variant: "error" });
        }
    };

    const openSaleDialog = (allocation) => {
        setSaleDialog(allocation);
        setSaleQty("");
    };

    const confirmSale = async () => {
        const qty = Number(saleQty);
        if (!qty || qty < 1) {
            enqueueSnackbar("Please enter a valid quantity", { variant: "error" });
            return;
        }
        setLoggingSale(true);
        try {
            await axios.post(`/api/v1/admin/vendor/${vendorId}/sale`, {
                storeId: saleDialog.store._id,
                productId: saleDialog.product._id,
                quantitySold: qty,
            });
            enqueueSnackbar("Sale logged", { variant: "success" });
            setSaleDialog(null);
            fetchAll();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to log sale", { variant: "error" });
        } finally {
            setLoggingSale(false);
        }
    };

    if (loading) return <BackdropLoader />;
    if (!vendor) return null;

    return (
        <div className="flex flex-col gap-4">
            <MetaData title={`Admin: ${vendor.name} | 1313 Mart`} />

            <Link to="/admin/vendors" className="flex items-center gap-0 font-medium text-primary-blue uppercase w-fit">
                <ArrowBackIosIcon sx={{ fontSize: "18px" }} />Back to Vendors
            </Link>

            <div className="bg-white shadow-lg rounded-lg p-6">
                <h1 className="text-lg font-medium">{vendor.name}{vendor.brandName && ` — ${vendor.brandName}`}</h1>
                <p className="text-sm text-gray-500">{vendor.email}{vendor.phone && ` · ${vendor.phone}`}</p>
            </div>

            <div className="bg-white shadow-lg rounded-lg p-6 flex flex-col gap-3">
                <h3 className="font-medium text-lg">Allocate Product to a Store</h3>
                <form onSubmit={allocateHandler} className="flex flex-wrap items-end gap-3">
                    <TextField select label="Store" size="small" value={storeId} onChange={(e) => setStoreId(e.target.value)} sx={{ minWidth: 200 }}>
                        {stores.map((s) => <MenuItem value={s._id} key={s._id}>{s.name}</MenuItem>)}
                    </TextField>
                    <Autocomplete
                        size="small"
                        options={productOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={selectedProduct}
                        onChange={(e, newValue) => setSelectedProduct(newValue)}
                        onInputChange={(e, newInput) => setProductSearch(newInput)}
                        sx={{ minWidth: 260 }}
                        renderInput={(params) => <TextField {...params} label="Product" placeholder="Search products..." />}
                    />
                    <TextField label="Quantity" type="number" size="small" value={quantity} onChange={(e) => setQuantity(e.target.value)} sx={{ width: 120 }} inputProps={{ min: 0 }} />
                    <button type="submit" disabled={allocating} className="bg-primary-orange px-5 py-2 text-white font-medium rounded shadow hover:shadow-lg disabled:opacity-60">
                        {allocating ? "Saving..." : "Allocate"}
                    </button>
                </form>
                <p className="text-xs text-gray-400">Allocating the same product to the same store again updates the existing quantity instead of duplicating it.</p>
            </div>

            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <h3 className="font-medium text-lg p-6 pb-0">Current Allocations</h3>
                <table className="w-full text-sm mt-3">
                    <thead>
                        <tr className="text-left text-gray-500 text-xs uppercase border-b">
                            <th className="px-6 py-2">Store</th>
                            <th className="px-6 py-2">Product</th>
                            <th className="px-6 py-2 text-right">Remaining Qty</th>
                            <th className="px-6 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allocations.map((a) => (
                            <tr key={a._id} className="border-b last:border-0">
                                <td className="px-6 py-3">{a.store?.name}</td>
                                <td className="px-6 py-3">{a.product?.name}</td>
                                <td className="px-6 py-3 text-right font-medium">{a.quantity}</td>
                                <td className="px-6 py-3 text-right">
                                    <button onClick={() => openSaleDialog(a)} className="text-primary-green mr-3" title="Log a sale">
                                        <PointOfSaleIcon sx={{ fontSize: "18px" }} />
                                    </button>
                                    <button onClick={() => deleteAllocation(a._id)} className="text-red-500" title="Remove allocation">
                                        <DeleteIcon sx={{ fontSize: "18px" }} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {allocations.length === 0 && (
                            <tr><td colSpan={4} className="px-6 py-6 text-center text-gray-400">No allocations yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <h3 className="font-medium text-lg p-6 pb-0">Sales History</h3>
                <table className="w-full text-sm mt-3">
                    <thead>
                        <tr className="text-left text-gray-500 text-xs uppercase border-b">
                            <th className="px-6 py-2">Date</th>
                            <th className="px-6 py-2">Store</th>
                            <th className="px-6 py-2">Product</th>
                            <th className="px-6 py-2 text-right">Qty Sold</th>
                            <th className="px-6 py-2">Logged By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sales.map((s) => (
                            <tr key={s._id} className="border-b last:border-0">
                                <td className="px-6 py-2.5">{new Date(s.soldAt).toLocaleString('en-HK')}</td>
                                <td className="px-6 py-2.5">{s.store?.name}</td>
                                <td className="px-6 py-2.5">{s.product?.name}</td>
                                <td className="px-6 py-2.5 text-right font-medium">{s.quantitySold}</td>
                                <td className="px-6 py-2.5 text-gray-400">{s.loggedBy}</td>
                            </tr>
                        ))}
                        {sales.length === 0 && (
                            <tr><td colSpan={5} className="px-6 py-6 text-center text-gray-400">No sales logged yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!saleDialog} onClose={() => setSaleDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Log a Sale</DialogTitle>
                <DialogContent className="flex flex-col gap-3">
                    <p className="text-sm text-gray-600">
                        {saleDialog?.product?.name} at {saleDialog?.store?.name} — {saleDialog?.quantity} currently remaining.
                    </p>
                    <TextField label="Quantity Sold" type="number" size="small" value={saleQty} onChange={(e) => setSaleQty(e.target.value)} autoFocus inputProps={{ min: 1 }} />
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setSaleDialog(null)} className="py-2 px-4 text-sm text-gray-600">Cancel</button>
                    <button onClick={confirmSale} disabled={loggingSale} className="py-2 px-5 bg-primary-green text-white text-sm font-medium rounded shadow hover:shadow-lg disabled:opacity-60">
                        {loggingSale ? "Logging..." : "Log Sale"}
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default VendorAllocations;